import { useState } from "react";
import { getCustomerById, getUserActivitySummary, getUserById, getUserMetrics, getUserReconciliationOutstanding, getUsers } from "../api/requests";
import { ISelectDropdownItem } from "../components/form-control/SelectDropdown";
import { IUser, User } from "../components/models/User";
import { IActivitySummary, IReconciliationOutstanding, IUserMetrics } from "../resources/interfaces";
import { ACTIVITY_SUMMARY_INIT_DATA } from "../views/dashboard/AgentDashboard";

function useUsers() {
    const [loadingUsers, setLoadingUsers] = useState<boolean>(true);
    const [users, setUsers] = useState<User[]>([]);
    const [refreshing, setRefreshing] = useState<boolean>(true);
    const [user, setUser] = useState<User>();
    const [metrics, setMetrics] = useState<IUserMetrics>({
        total_customers: 0,
        total_accounts: 0,
        total_deposits_count: 0,
        total_withdrawals_count: 0,
        total_deposits_amount: 0,
        total_withdrawals_amount: 0,
        total_cash: 0,
        total_cash_count: 0,
        total_customers_engaged: 0
    })

    const [reconciliationOutstanding, setReconciliationOutstanding] = useState<IReconciliationOutstanding>({
        total_outstanding: 0,
        total_amount_outstanding: 0,
        transactions_outstanding: []
    })

    const [activitySummary, setActivitySummary] = useState<IActivitySummary>(ACTIVITY_SUMMARY_INIT_DATA)

    const USERS_LIST_FOR_FORM = users.map((user: User): ISelectDropdownItem => (
        {
            id: user.id,
            name: user.fullName,
            value: user.id,
        }
    ))

    const NON_AGENT_USERS_LIST_FOR_FORM = users.filter((user: User) => !user.isAgent()).map((user: User): ISelectDropdownItem => (
        {
            id: user.id,
            name: user.fullName,
            value: user.id,
        }
    ))

    const fetchUsers = ({ 
        query = "", 
        companyId, 
        limit, 
        branchId, 
        isActive 
    }: { 
        query?: string, 
        companyId?: string, 
        limit?: number, 
        branchId?: string, 
        isActive?: boolean
    }) => {
        setRefreshing(true);
        getUsers(query, companyId, limit, branchId, isActive)
            .then((response: any) => {
                const _users = response?.map((_user: IUser) => new User(_user));
                setUsers(_users);
            }).catch((error: any) => {
                console.log("request error", error);
            }).finally(() => {
                setLoadingUsers(false);
                setRefreshing(false);
            })
    }

    const fetchUserById = (id: string) => {
        getUserById(id)
            .then((response: any) => {
                setUser(new User(response));
            }).catch((error: any) => {
                console.log("request error", error);
            }).finally(() => {
                setLoadingUsers(false);
                setRefreshing(false);
            })
    }

    const fetchUserMetrics = (id?: string, from?: string, to?: string) => {
        getUserMetrics(id, from, to)
            .then((response: any) => {
                setMetrics(response);
            }).catch((error: any) => {
                console.log("request error", error);
            }).finally(() => {
                setLoadingUsers(false);
                setRefreshing(false);
            })
    }

    const fetchUserReconciliationOutstanding = (id: string) => {
        getUserReconciliationOutstanding(id)
            .then((response: any) => {
                setReconciliationOutstanding(response);
            }).catch((error: any) => {
                console.log("request error", error);
            }).finally(() => {
                setLoadingUsers(false);
                setRefreshing(false);
            })
    }

    const fetchActivitySummary = (id?: string) => {
        getUserActivitySummary(id)
            .then((response: any) => {
                setActivitySummary(response);
            }).catch((error: any) => {
                console.log("request error", error);
            }).finally(() => {
                setLoadingUsers(false);
                setRefreshing(false);
            })
    }

    return {
        users,
        loadingUsers,
        USERS_LIST_FOR_FORM,
        refreshing,
        fetchUsers,
        user,
        fetchUserById,
        setUsers,
        setUser,
        fetchUserMetrics,
        metrics,
        fetchUserReconciliationOutstanding,
        reconciliationOutstanding,
        NON_AGENT_USERS_LIST_FOR_FORM,
        fetchActivitySummary,
        activitySummary
    };

}

export default useUsers;